import { supabase } from '@/integrations/supabase/client';
import type {
  ProjectTeamMember,
  ProjectTeamMemberInsert,
  ProjectTeamMemberUpdate,
} from '@/types/project.types';
import { projectService } from './projectService';

export const teamMemberService = {
  // Fetch all team members for a project
  async fetchProjectTeamMembers(projectId: string): Promise<ProjectTeamMember[]> {
    const { data, error } = await supabase
      .from('project_team_members')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data as ProjectTeamMember[];
  },

  // Fetch active team members for a project
  async fetchActiveTeamMembers(projectId: string): Promise<ProjectTeamMember[]> {
    const { data, error } = await supabase
      .from('project_team_members')
      .select('*')
      .eq('project_id', projectId)
      .eq('status', 'active')
      .order('member_name', { ascending: true });

    if (error) throw error;
    return data as ProjectTeamMember[];
  },

  // Fetch a single team member
  async fetchTeamMemberById(id: string): Promise<ProjectTeamMember | null> {
    const { data, error } = await supabase
      .from('project_team_members')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ProjectTeamMember;
  },

  // Add a team member to a project
  async addTeamMember(member: ProjectTeamMemberInsert): Promise<ProjectTeamMember> {
    const { data, error } = await supabase
      .from('project_team_members')
      .insert(member)
      .select()
      .single();

    if (error) throw error;

    // Log activity
    await projectService.logActivity({
      project_id: member.project_id,
      activity_type: 'team_member_added',
      title: 'Team Member Added',
      description: `${member.member_name} (${member.role}) added to the project`,
      metadata: {
        member_type: member.member_type,
        hourly_rate: member.hourly_rate,
        total_budget: member.total_budget,
      },
    });

    return data as ProjectTeamMember;
  },

  // Update a team member
  async updateTeamMember(id: string, update: ProjectTeamMemberUpdate): Promise<ProjectTeamMember> {
    // Get current member for activity logging
    const { data: currentMember } = await supabase
      .from('project_team_members')
      .select('*')
      .eq('id', id)
      .single();

    const { data, error } = await supabase
      .from('project_team_members')
      .update(update)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Log activity
    if (currentMember && data) {
      let activityDescription = `${data.member_name}'s information updated`;
      
      if (update.status && update.status !== currentMember.status) {
        if (update.status === 'inactive') {
          activityDescription = `${data.member_name} marked as inactive`;
        } else if (update.status === 'completed') {
          activityDescription = `${data.member_name} completed their work on the project`;
        }
      }

      await projectService.logActivity({
        project_id: data.project_id,
        activity_type: 'team_member_updated',
        title: 'Team Member Updated',
        description: activityDescription,
      });
    }

    return data as ProjectTeamMember;
  },

  // Remove a team member
  async removeTeamMember(id: string): Promise<void> {
    // Get member info for activity logging
    const { data: member } = await supabase
      .from('project_team_members')
      .select('project_id, member_name, role')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('project_team_members')
      .delete()
      .eq('id', id);

    if (error) throw error;

    if (member) {
      await projectService.logActivity({
        project_id: member.project_id,
        activity_type: 'team_member_removed',
        title: 'Team Member Removed',
        description: `${member.member_name} (${member.role}) removed from the project`,
      });
    }
  },

  // Calculate team costs
  async calculateTeamCosts(projectId: string): Promise<{
    internal_cost: number;
    freelancer_cost: number;
    contractor_cost: number;
    vendor_cost: number;
    total_cost: number;
    cost_breakdown: Array<{
      member_name: string;
      member_type: string;
      cost: number;
      hours_worked?: number;
    }>;
  }> {
    const teamMembers = await this.fetchProjectTeamMembers(projectId);
    
    // Also fetch time entries to calculate internal costs
    const { data: timeEntries } = await supabase
      .from('project_time_entries')
      .select('user_id, hours_logged, billing_rate')
      .eq('project_id', projectId);

    const costs = {
      internal_cost: 0,
      freelancer_cost: 0,
      contractor_cost: 0,
      vendor_cost: 0,
      total_cost: 0,
      cost_breakdown: [] as Array<{
        member_name: string;
        member_type: string;
        cost: number;
        hours_worked?: number;
      }>,
    };

    // Calculate costs for team members
    teamMembers.forEach(member => {
      let memberCost = 0;
      
      if (member.total_budget) {
        memberCost = member.total_budget;
      } else if (member.member_type === 'internal' && timeEntries) {
        // For internal members, calculate from time entries
        const memberTimeEntries = timeEntries.filter(entry => 
          // This would need to map user_id to team member somehow
          // For now, we'll skip this complexity
          false
        );
        const hours = memberTimeEntries.reduce((sum, entry) => sum + (entry.hours_logged || 0), 0);
        const rate = member.hourly_rate || 0;
        memberCost = hours * rate;
      }

      switch (member.member_type) {
        case 'internal':
          costs.internal_cost += memberCost;
          break;
        case 'freelancer':
          costs.freelancer_cost += memberCost;
          break;
        case 'contractor':
          costs.contractor_cost += memberCost;
          break;
        case 'vendor':
          costs.vendor_cost += memberCost;
          break;
      }

      costs.cost_breakdown.push({
        member_name: member.member_name,
        member_type: member.member_type,
        cost: memberCost,
      });
    });

    costs.total_cost = costs.internal_cost + costs.freelancer_cost + 
                      costs.contractor_cost + costs.vendor_cost;

    return costs;
  },

  // Get team member availability
  async checkMemberAvailability(memberId: string): Promise<{
    is_available: boolean;
    current_projects: Array<{
      project_id: string;
      project_name: string;
      role: string;
      end_date?: string;
    }>;
  }> {
    // Get member details
    const { data: member } = await supabase
      .from('project_team_members')
      .select('member_email')
      .eq('id', memberId)
      .single();

    if (!member || !member.member_email) {
      return { is_available: true, current_projects: [] };
    }

    // Find all active projects for this member
    const { data: assignments } = await supabase
      .from('project_team_members')
      .select(`
        project_id,
        role,
        end_date,
        project:project_id (name)
      `)
      .eq('member_email', member.member_email)
      .eq('status', 'active');

    const currentProjects = assignments?.map(a => ({
      project_id: a.project_id,
      project_name: (a.project as any)?.name || 'Unknown Project',
      role: a.role,
      end_date: a.end_date,
    })) || [];

    // Consider available if working on less than 3 projects
    const isAvailable = currentProjects.length < 3;

    return {
      is_available: isAvailable,
      current_projects: currentProjects,
    };
  },

  // Send contract to freelancer/contractor
  async sendContract(memberId: string, contractDetails: string): Promise<void> {
    const member = await this.fetchTeamMemberById(memberId);
    
    if (!member) throw new Error('Team member not found');

    // Update member with contract details
    await this.updateTeamMember(memberId, {
      contract_details: contractDetails,
    });

    // Log activity
    await projectService.logActivity({
      project_id: member.project_id,
      activity_type: 'contract_sent',
      title: 'Contract Sent',
      description: `Contract sent to ${member.member_name}`,
      metadata: {
        member_type: member.member_type,
        member_email: member.member_email,
      },
    });
  },

  // Track payment to team member
  async trackPayment(memberId: string, paymentInfo: {
    amount: number;
    payment_date: string;
    payment_method: string;
    invoice_number?: string;
  }): Promise<void> {
    const member = await this.fetchTeamMemberById(memberId);
    
    if (!member) throw new Error('Team member not found');

    // Update member notes with payment info
    const existingNotes = member.notes || '';
    const paymentNote = `Payment of $${paymentInfo.amount} made on ${paymentInfo.payment_date} via ${paymentInfo.payment_method}`;
    const updatedNotes = existingNotes ? `${existingNotes}\n\n${paymentNote}` : paymentNote;

    await this.updateTeamMember(memberId, {
      notes: updatedNotes,
    });

    // Log activity
    await projectService.logActivity({
      project_id: member.project_id,
      activity_type: 'payment_made',
      title: 'Payment Made',
      description: `Payment of $${paymentInfo.amount} made to ${member.member_name}`,
      metadata: paymentInfo,
    });
  },
};